import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {HTMLButtonElement | null} */
  #mobileTrigger = null;

  /** @type {MediaQueryList | null} */
  #mobileMedia = null;

  /** @type {number} */
  #mobileScrollY = 0;

  /** @type {number | null} */
  #closeTimer = null;

  /** @type {number | null} */
  #resetTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
    this.#setupMobileMaterialDrawer();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#mobileMedia?.removeEventListener('change', this.#handleViewportChange);

    if (this.#closeTimer !== null) window.clearTimeout(this.#closeTimer);
    if (this.#resetTimer !== null) window.clearTimeout(this.#resetTimer);

    this.#unlockMobilePage();
  }

  /**
   * Move only the mobile drawer to the body and leave a matching trigger in the header.
   * The complete Shopify page surface can then move as one object, including the header.
   */
  #setupMobileMaterialDrawer() {
    if (!this.classList.contains('header__drawer--mobile')) return;

    this.#mobileMedia = window.matchMedia('(max-width: 749px)');
    this.#mobileMedia.removeEventListener('change', this.#handleViewportChange);
    this.#mobileMedia.addEventListener('change', this.#handleViewportChange);

    if (this.dataset.dlbMobilePortaled === 'true') return;

    const host = this.closest('.header__drawer');
    const originalSummary = this.refs.details.querySelector(':scope > summary');
    if (!(host instanceof HTMLElement) || !(originalSummary instanceof HTMLElement)) return;

    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = `${originalSummary.className} dlb-mobile-menu-trigger`;
    trigger.setAttribute('aria-label', originalSummary.getAttribute('aria-label') ?? 'Menu');
    trigger.setAttribute('aria-controls', this.refs.details.id);
    trigger.setAttribute('aria-expanded', 'false');
    trigger.innerHTML = originalSummary.innerHTML;
    trigger.addEventListener('click', this.#toggleFromMobileTrigger);

    host.insertBefore(trigger, this);
    this.#mobileTrigger = trigger;
    this.dataset.dlbMobilePortaled = 'true';
    this.classList.add('dlb-mobile-menu-portal');

    document.querySelectorAll('body > header-drawer.dlb-mobile-menu-portal').forEach((drawer) => {
      if (drawer !== this) drawer.remove();
    });

    document.body.append(this);
  }

  #toggleFromMobileTrigger = () => {
    if (!this.#mobileMedia?.matches) return;

    if (this.isOpen) {
      this.close();
      return;
    }

    this.refs.details.setAttribute('open', '');
    this.open();
  };

  #handleViewportChange = (event) => {
    if (event.matches) return;

    if (this.#closeTimer !== null) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }

    if (this.#resetTimer !== null) {
      window.clearTimeout(this.#resetTimer);
      this.#resetTimer = null;
    }

    this.refs.details.classList.remove('dlb-mobile-menu-closing');
    this.#unlockMobilePage();
    this.#mobileTrigger?.setAttribute('aria-expanded', 'false');

    if (this.isOpen) {
      reset(this.refs.details);
      removeTrapFocus();
    }
  };

  /**
   * Close Shopify overlays before opening the navigation drawer. Dialogs such
   * as predictive search otherwise remain full-size outside the transformed
   * page and appear as a second close button / pale strip.
   */
  #closeCompetingOverlays() {
    document.querySelectorAll('dialog[open]').forEach((dialog) => {
      try {
        dialog.close();
      } catch {
        dialog.removeAttribute('open');
      }

      dialog.classList.remove('dialog-closing');
    });

    document.querySelectorAll('details[open]').forEach((details) => {
      if (details === this.refs.details || this.refs.details.contains(details)) return;

      details.removeAttribute('open');
      details.classList.remove('menu-open');
      details.querySelector(':scope > summary')?.setAttribute('aria-expanded', 'false');
    });
  }

  #lockMobilePage() {
    if (document.body.classList.contains('dlb-mobile-scroll-lock')) return;

    this.#mobileScrollY = window.scrollY || document.documentElement.scrollTop || 0;

    const pageInner = document.getElementById('DlbMobilePageShellInner');
    if (pageInner instanceof HTMLElement) {
      pageInner.style.transform = `translate3d(0, -${this.#mobileScrollY}px, 0)`;
    }

    document.documentElement.classList.add('dlb-mobile-scroll-lock');
    document.body.classList.add('dlb-mobile-scroll-lock');
  }

  #setMobilePageOpen(isOpen) {
    document.documentElement.classList.toggle('dlb-mobile-menu-open', isOpen);
    document.body.classList.toggle('dlb-mobile-menu-open', isOpen);
  }

  #unlockMobilePage() {
    const wasLocked = document.body.classList.contains('dlb-mobile-scroll-lock');
    const scrollY = this.#mobileScrollY;
    const pageInner = document.getElementById('DlbMobilePageShellInner');

    this.#setMobilePageOpen(false);
    document.documentElement.classList.remove('dlb-mobile-scroll-lock');
    document.body.classList.remove('dlb-mobile-scroll-lock');

    if (pageInner instanceof HTMLElement) {
      pageInner.style.removeProperty('transform');
    }

    if (wasLocked) {
      window.scrollTo(0, scrollY);
    }
  }

  /**
   * Close the main menu drawer when the Escape key is pressed.
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /** @returns {boolean} Whether the main menu drawer is open. */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target.
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /** Toggle the main menu drawer. */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer.
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    details.classList.remove('dlb-mobile-menu-closing');
    summary.setAttribute('aria-expanded', 'true');
    this.#mobileTrigger?.setAttribute('aria-expanded', 'true');

    if (details === this.refs.details && this.#mobileMedia?.matches) {
      this.#closeCompetingOverlays();
      this.#lockMobilePage();
    }

    requestAnimationFrame(() => {
      if (details === this.refs.details && this.#mobileMedia?.matches) {
        this.#setMobilePageOpen(true);
      }

      details.classList.add('menu-open');
      window.setTimeout(() => trapFocus(details), 0);
    });
  }

  /**
   * Go back or close the main menu drawer.
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /** Close the main menu drawer. */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open.
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const isMainMobile = details === this.refs.details && this.#mobileMedia?.matches;

    if (isMainMobile && details.classList.contains('menu-open')) {
      if (details.classList.contains('dlb-mobile-menu-closing')) return;

      details.classList.add('dlb-mobile-menu-closing');

      this.#closeTimer = window.setTimeout(() => {
        this.#closeTimer = null;
        this.#completeClose(details, true);
      }, 300);
      return;
    }

    this.#completeClose(details, false);
  }

  /**
   * Finish the close after the close control's pop animation.
   * @param {HTMLDetailsElement} details
   * @param {boolean} isMainMobile
   */
  #completeClose(details, isMainMobile) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('dlb-mobile-menu-closing');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      this.#mobileTrigger?.setAttribute('aria-expanded', 'false');

      if (isMainMobile) {
        // Return the fixed viewport surface to full size first. Keep the page
        // locked until that 800ms transition is complete, otherwise Shopify
        // snaps back to the live document during the animation.
        this.#setMobilePageOpen(false);
      }
    }

    const finishReset = () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        this.querySelectorAll('details[open]').forEach(reset);
        this.#unlockMobilePage();
      } else {
        window.setTimeout(() => trapFocus(this.refs.details), 0);
      }
    };

    if (isMainMobile) {
      if (this.#resetTimer !== null) window.clearTimeout(this.#resetTimer);
      this.#resetTimer = window.setTimeout(() => {
        this.#resetTimer = null;
        finishReset();
      }, 820);
      return;
    }

    onAnimationEnd(details, finishReset);
  }

  /**
   * Remove will-change after each menu-item entrance animation.
   */
  #setupAnimatedElementListeners() {
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }

    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state.
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open', 'dlb-mobile-menu-closing');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
